import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  Box,
  Input,
  Textarea,
  Select,
  VStack,
  FormControl,
  FormLabel,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  HStack,
  useToast,
  Flex,
  Button,
  FormErrorMessage,
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import ImageGallery from "../../components/ImageGallery";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import MobilePushPreview from "./MobilePushPreview";
import Flatpickr from "react-flatpickr";
import { saveAppPushTemplate, getMobilePushTemplateList } from "../../Services/api";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const buttonActions = [
  { value: "NAVIGATE", label: "Navigate to a Screen", message: "To take the user to a certain navigation screen" },
  { value: "LANDING", label: "Landing Page", message: "To take the user to a rich landing page" },
  { value: "SHARE", label: "Share", message: "To let users share the notification directly" },
  { value: "COPY", label: "Copy", message: "To copy a code/text to clipboard" },
];

const valueToLabel = (v) => (buttonActions.find((a) => a.value === v) || {}).label || "Navigate to a Screen";

const MAX_CAROUSEL_IMAGES = 3;

const isValidUrl = (url) => {
  if (!url || url.trim() === "") return false;
  try {
    new URL(url.trim());
    return true;
  } catch {
    return false;
  }
};

const MobilePushCreateTemplate = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const location = useLocation();
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  const formik = useFormik({
    initialValues: {
      uuid: undefined,
      templateName: "",
      templateTitle: "",
      timerDuration: new Date(),
      templateMessage: "",
      notificationDataMap: {},
      templateType: { type: "image", pushType: "pushNotification" },
      templateImageUrl: [""],
      buttons: [],
      type: { type: "media", pushType: "pushNotification" },
      isTimer: false,
      targetPlatform: "android",
    },
    validate: (values) => {
      const errors = {};

      // Required basic fields
      if (!values.templateName.trim()) errors.templateName = "Template Name is required";
      if (!values.templateTitle.trim()) errors.templateTitle = "Template Title is required";

      // Type-specific validation
      if (values.type.type === "media" || values.type.type === "timer") {
        if (!values.templateImageUrl || values.templateImageUrl.length === 0) {
          errors.templateImageUrl = "At least one image is required";
        } else if (values.templateType.type === "image") {
          const img = values.templateImageUrl[0];
          if (!img || !isValidUrl(img)) {
            errors.templateImageUrl = "Valid image URL is required";
          }
        } else if (values.templateType.type === "carousel") {
          const validImages = values.templateImageUrl.filter((url) => url && isValidUrl(url));
          if (validImages.length === 0) {
            errors.templateImageUrl = "At least one valid image URL is required";
          }
          if (values.templateImageUrl.length < 2) {
            errors.templateImageUrl = "Carousel must have at least 2 images";
          }
        }
      }

      // Timer validation
      if (values.type.type === "timer") {
        if (!values.timerDuration || !(values.timerDuration instanceof Date) || isNaN(values.timerDuration)) {
          errors.timerDuration = "Valid timer duration is required";
        } else if (values.timerDuration <= new Date()) {
          errors.timerDuration = "Timer must be set in the future";
        }
      }

      // Buttons validation - if any button exists, all its required fields must be filled
      if (values.buttons && values.buttons.length > 0) {
        const buttonErrors = [];

        values.buttons.forEach((button, index) => {
          const btnErr = {};
          if (!button.buttonName?.trim()) {
            btnErr.buttonName = "Button name is required";
          }
          if (!button.buttonAction) {
            btnErr.buttonAction = "Button action is required";
          }

          if (button.buttonAction === "NAVIGATE" || button.buttonAction === "LANDING") {
            if (!button.buttonUrl?.trim() || !isValidUrl(button.buttonUrl)) {
              btnErr.buttonUrl = "Valid URL is required for Navigate/Landing";
            }
          }

          if (button.buttonAction === "COPY" && !button.copyText?.trim()) {
            btnErr.copyText = "Copy text is required for Copy action";
          }

          if (Object.keys(btnErr).length > 0) {
            buttonErrors[index] = btnErr;
          }
        });

        if (buttonErrors.length > 0) {
          errors.buttons = buttonErrors;
        }
      }

      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        let normalizedImages = values.templateImageUrl || [""];

        if (values.type.type === "text") {
          normalizedImages = [];
        } else if (values.templateType.type === "image") {
          normalizedImages = [normalizedImages[0] || ""].filter(Boolean);
        } else {
          normalizedImages = (normalizedImages || []).filter((img) => img && isValidUrl(img)).slice(0, MAX_CAROUSEL_IMAGES);
        }

        const payload = {
          ...values,
          uuid: uuid || values.uuid,
          templateImageUrl: normalizedImages,
          targetPlatform: [values.targetPlatform],
          timerDuration: values.isTimer && values.timerDuration instanceof Date
            ? values.timerDuration.toISOString()
            : undefined,
          buttons: values.buttons.filter(b => b.buttonName.trim()), // remove empty buttons
        };

        if (!payload.isTimer) {
          delete payload.timerDuration;
        }

        const resp = await saveAppPushTemplate(payload);

        if (resp) {
          toast({
            title: "Success",
            description: "Template saved successfully!",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          navigate("/mobilepush/list");
        }
      } catch (error) {
        toast({
          title: "Error",
          description: error?.message || "Failed to save template",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Helper to get nested error
  const getError = (field) => {
    return formik.touched[field] && formik.errors[field] ? formik.errors[field] : null;
  };

  const getButtonError = (index, field) => {
    return formik.touched.buttons?.[index]?.[field] && formik.errors.buttons?.[index]?.[field]
      ? formik.errors.buttons[index][field]
      : null;
  };

  const safeParseJSON = (val, fallback) => {
    try {
      return typeof val === "string" ? JSON.parse(val) : val ?? fallback;
    } catch {
      return fallback;
    }
  };

  const normalizePlatformToSingle = (p) => {
    if (Array.isArray(p)) return p[0] || "android";
    if (typeof p === "string") {
      try {
        const parsed = JSON.parse(p);
        if (Array.isArray(parsed)) return parsed[0] || "android";
      } catch {}
      return p || "android";
    }
    return "android";
  };

  const apiToForm = (row) => {
    if (!row) return null;

    const templateType = safeParseJSON(row.templateType, { type: "image", pushType: "pushNotification" });
    const images = safeParseJSON(row.templateImageUrl, [""]);
    const buttonsRaw = safeParseJSON(row.buttons, []);

    const buttons = (buttonsRaw || []).filter(b => b.buttonName).map((b) => ({
      buttonName: b.buttonName || "",
      buttonAction: buttonActions.find(a => a.value === b.buttonAction)?.value || "NAVIGATE",
      buttonActionLabel: valueToLabel(b.buttonAction),
      buttonUrl: b.buttonUrl || "",
      copyText: b.copyText || "",
      couponCode: b.couponCode || "",
    }));

    const formType = templateType?.type === "text" ? "text" : templateType?.type === "timer" ? "timer" : "media";

    return {
      uuid: row.uuid,
      templateName: row.templateName || "",
      templateTitle: row.templateTitle || "",
      templateMessage: row.templateMessage || "",
      notificationDataMap: safeParseJSON(row.templateNotificationData, {}),
      templateType: { type: templateType?.type || "image", pushType: "pushNotification" },
      templateImageUrl: Array.isArray(images) ? images : [images || ""],
      buttons: buttons.length > 0 ? buttons : [],
      type: { type: formType, pushType: row.type?.pushType || "pushNotification" },
      isTimer: formType === "timer",
      targetPlatform: normalizePlatformToSingle(row.targetPlatform),
      timerDuration: row.timerDuration ? new Date(row.timerDuration) : new Date(),
    };
  };

  useEffect(() => {
    const stateTemplate = location.state && (location.state.template || location.state);
    if (stateTemplate) {
      const parsed = apiToForm(stateTemplate);
      if (parsed) formik.setValues(parsed);
      return;
    }

    const load = async () => {
      if (!uuid) return;
      try {
        setIsLoadingTemplate(true);
        const resp = await getMobilePushTemplateList(1, 1000);
        const items = resp?.data?.data || resp?.data || [];
        const match = items.find((x) => x.uuid === uuid);
        if (match) {
          const parsed = apiToForm(match);
          if (parsed) formik.setValues(parsed);
        }
      } catch (e) {
        toast({ title: "Failed to load template", status: "error" });
      } finally {
        setIsLoadingTemplate(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const setSingleImage = (val) => formik.setFieldValue("templateImageUrl[0]", val).then(() => formik.validateField("templateImageUrl"));
  const setCarouselImageAt = (idx, val) => {
    const arr = [...formik.values.templateImageUrl];
    arr[idx] = val;
    formik.setFieldValue("templateImageUrl", arr).then(() => formik.validateField("templateImageUrl"));
  };

  const addCarouselImage = () => {
    if (formik.values.templateImageUrl.length >= MAX_CAROUSEL_IMAGES) return;
    formik.setFieldValue("templateImageUrl", [...formik.values.templateImageUrl, ""]);
  };

  const removeCarouselImage = (idx) => {
    if (formik.values.templateImageUrl.length <= 2 && formik.values.templateType.type === "carousel") {
      toast({ title: "Carousel needs at least 2 images", status: "warning" });
      return;
    }
    const arr = formik.values.templateImageUrl.filter((_, i) => i !== idx);
    formik.setFieldValue("templateImageUrl", arr);
  };

  const isTextType = formik.values.type.type === "text";
  const isValid = Object.keys(formik.errors).length === 0 && formik.dirty;

  return (
    <HStack gap={"10vw"} w={"100%"} align="flex-start">
      <Box mt={8} width={"50%"} p={4}>
        <Text fontSize={"1.5vw"} fontWeight="bold">{uuid ? "Edit Template" : "Create Template"}</Text>

        <form onSubmit={formik.handleSubmit}>
          <Accordion allowToggle defaultIndex={[0, 1]} mt={6}>
            {/* Template Information */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="semibold">Template Information</Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <VStack spacing={4}>
                  <FormControl isRequired isInvalid={!!getError("templateName")}>
                    <FormLabel>Template Name</FormLabel>
                    <Input
                      borderRadius={"16px"}
                      bg={"#ebebeb"}
                      name="templateName"
                      value={formik.values.templateName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FormErrorMessage>{getError("templateName")}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!getError("templateTitle")}>
                    <FormLabel>Template Title</FormLabel>
                    <Input
                      borderRadius={"16px"}
                      bg={"#ebebeb"}
                      name="templateTitle"
                      value={formik.values.templateTitle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FormErrorMessage>{getError("templateTitle")}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Template Message</FormLabel>
                    <Textarea
                      name="templateMessage"
                      value={formik.values.templateMessage}
                      onChange={formik.handleChange}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Target Platform</FormLabel>
                    <Select
                      borderRadius={"16px"}
                      bg={"#ebebeb"}
                      name="targetPlatform"
                      value={formik.values.targetPlatform}
                      onChange={formik.handleChange}
                    >
                      <option value="android">Android</option>
                      <option value="ios">iOS</option>
                    </Select>
                  </FormControl>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            {/* Notification Type & Media */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="semibold">Notification Type & Media</Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Notification Type</FormLabel>
                    <Select
                      borderRadius={"16px"}
                      bg={"#ebebeb"}
                      value={formik.values.type.type}
                      onChange={(e) => {
                        const val = e.target.value;
                        formik.setFieldValue("type.type", val);
                        formik.setFieldValue("isTimer", val === "timer");
                        if (val === "text") {
                          formik.setFieldValue("templateImageUrl", []);
                        } else if (val === "timer") {
                          formik.setFieldValue("templateType.type", "image");
                        }
                        formik.validateForm();
                      }}
                    >
                      <option value="text">Text Only</option>
                      <option value="media">Media</option>
                      <option value="timer">Timer</option>
                    </Select>
                  </FormControl>

                  {!isTextType && (
                    <>
                      {(formik.values.type.type === "media") && (
                        <FormControl>
                          <FormLabel>Media Type</FormLabel>
                          <Select
                            borderRadius={"16px"}
                            bg={"#ebebeb"}
                            value={formik.values.templateType.type}
                            onChange={(e) => {
                              const val = e.target.value;
                              formik.setFieldValue("templateType.type", val);
                              if (val === "image") {
                                formik.setFieldValue("templateImageUrl", [formik.values.templateImageUrl[0] || ""]);
                              }
                              formik.validateForm();
                            }}
                          >
                            <option value="image">Single Image</option>
                            <option value="carousel">Carousel</option>
                          </Select>
                        </FormControl>
                      )}

                      {/* Timer Duration */}
                      {formik.values.type.type === "timer" && (
                        <FormControl isRequired isInvalid={!!getError("timerDuration")}>
                          <FormLabel>Countdown Ends At</FormLabel>
                          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={2}>
                            <Flatpickr
                              options={{ enableTime: true, minDate: "today", dateFormat: "M d Y, h:i K" }}
                              value={formik.values.timerDuration}
                              onChange={([date]) => {
                                formik.setFieldValue("timerDuration", date);
                                formik.validateField("timerDuration");
                              }}
                            />
                          </Box>
                          <FormErrorMessage>{getError("timerDuration")}</FormErrorMessage>
                        </FormControl>
                      )}

                      {/* Image Inputs */}
                      {(formik.values.type.type === "media" || formik.values.type.type === "timer") && (
                        <FormControl isInvalid={!!getError("templateImageUrl")}>
                          {formik.values.templateType.type === "image" ? (
                            <>
                              <FormLabel>Image URL (Required)</FormLabel>
                              <Input
                                borderRadius={"16px"}
                                bg={"#ebebeb"}
                                value={formik.values.templateImageUrl[0] || ""}
                                onChange={(e) => setSingleImage(e.target.value)}
                                placeholder="https://..."
                              />
                              <ImageGallery setImageData={(v) => setSingleImage(v.mediaFileUrl)}
                                renderComponent={<Text color="blue.500" cursor="pointer" textDecor="underline" mt={2}>Choose from Gallery +</Text>}
                              />
                            </>
                          ) : (
                            <>
                              <FormLabel>Carousel Images (min 2)</FormLabel>
                              {formik.values.templateImageUrl.map((url, i) => (
                                <Flex key={i} gap={3} align="center" mb={3}>
                                  <Input
                                    flex={1}
                                    borderRadius={"16px"}
                                    bg={"#ebebeb"}
                                    value={url || ""}
                                    onChange={(e) => setCarouselImageAt(i, e.target.value)}
                                    placeholder="https://..."
                                  />
                                  {i > 0 && (
                                    <Button size="sm" colorScheme="red" onClick={() => removeCarouselImage(i)}>
                                      <FaTrash />
                                    </Button>
                                  )}
                                </Flex>
                              ))}
                              {formik.values.templateImageUrl.length < MAX_CAROUSEL_IMAGES && (
                                <Text color="blue.500" cursor="pointer" textDecor="underline" onClick={addCarouselImage}>
                                  + Add Image
                                </Text>
                              )}
                              <ImageGallery setImageData={(v) => setCarouselImageAt(formik.values.templateImageUrl.length - 1, v.mediaFileUrl)}
                                renderComponent={<Text color="blue.500" cursor="pointer" textDecor="underline" mt={2}>Choose from Gallery +</Text>}
                              />
                            </>
                          )}
                          <FormErrorMessage>{getError("templateImageUrl")}</FormErrorMessage>
                        </FormControl>
                      )}
                    </>
                  )}
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            {/* Buttons */}
            {formik.values.type.type !== "text" && (
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="semibold">Action Buttons</Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  {formik.values.buttons.map((btn, idx) => (
                    <Box key={idx} border="1px" borderColor="gray.200" borderRadius="md" p={4} mb={4}>
                      <Flex justify="space-between" mb={3}>
                        <Text fontWeight="medium">Button {idx + 1}</Text>
                        <Button size="sm" colorScheme="red" onClick={() => {
                          formik.setFieldValue("buttons", formik.values.buttons.filter((_, i) => i !== idx));
                        }}>
                          Remove
                        </Button>
                      </Flex>

                      <FormControl isInvalid={!!getButtonError(idx, "buttonName")} mb={3}>
                        <FormLabel>Button Text</FormLabel>
                        <Input
                          name={`buttons[${idx}].buttonName`}
                          value={btn.buttonName}
                          onChange={formik.handleChange}
                        />
                        <FormErrorMessage>{getButtonError(idx, "buttonName")}</FormErrorMessage>
                      </FormControl>

                      <FormControl isInvalid={!!getButtonError(idx, "buttonAction")} mb={3}>
                        <FormLabel>Action</FormLabel>
                        <Select
                          name={`buttons[${idx}].buttonAction`}
                          value={btn.buttonAction}
                          onChange={(e) => {
                            formik.handleChange(e);
                            formik.setFieldValue(`buttons[${idx}].buttonActionLabel`, valueToLabel(e.target.value));
                          }}
                        >
                          {buttonActions.map((a) => (
                            <option key={a.value} value={a.value}>{a.label}</option>
                          ))}
                        </Select>
                      </FormControl>

                      {(btn.buttonAction === "NAVIGATE" || btn.buttonAction === "LANDING") && (
                        <FormControl isInvalid={!!getButtonError(idx, "buttonUrl")} mb={3}>
                          <FormLabel>URL</FormLabel>
                          <Input
                            name={`buttons[${idx}].buttonUrl`}
                            value={btn.buttonUrl}
                            onChange={formik.handleChange}
                            placeholder="https://..."
                          />
                          <FormErrorMessage>{getButtonError(idx, "buttonUrl")}</FormErrorMessage>
                        </FormControl>
                      )}

                      {btn.buttonAction === "COPY" && (
                        <FormControl isInvalid={!!getButtonError(idx, "copyText")} mb={3}>
                          <FormLabel>Text to Copy</FormLabel>
                          <Input
                            name={`buttons[${idx}].copyText`}
                            value={btn.copyText}
                            onChange={formik.handleChange}
                          />
                          <FormErrorMessage>{getButtonError(idx, "copyText")}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Box>
                  ))}

                  <Text
                    color="blue.600"
                    cursor="pointer"
                    textDecor="underline"
                    onClick={() => formik.setFieldValue("buttons", [
                      ...formik.values.buttons,
                      { buttonName: "", buttonAction: "NAVIGATE", buttonUrl: "", copyText: "" }
                    ])}
                  >
                    + Add Button
                  </Text>
                </AccordionPanel>
              </AccordionItem>
            )}

            {/* Submit */}
          </Accordion>

          <PrimaryButton
            type="submit"
            mt={6}
            float="right"
            label={formik.isSubmitting ? "Saving..." : "Save Template"}
            isDisabled={!isValid || formik.isSubmitting}
          />
        </form>
      </Box>

      <Box position="sticky" top={4}>
        <MobilePushPreview type="mobile" formData={formik.values} />
      </Box>
    </HStack>
  );
};

export default MobilePushCreateTemplate;